import React, { useEffect, useState } from 'react';

import AttachFileIcon from '@mui/icons-material/AttachFile';
import UploadIcon from '@mui/icons-material/Upload';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import AlertDialog from 'components/UI/AlertDialog';
import InfoDialog from 'components/UI/InfoDialog';
import { useGetBusinessUnitByIdMutation } from 'services/api/businessUnits.api';
import { useMapCSVHeadersMutation, useUploadClientsFromCSVMutation } from 'services/api/clients.api';

import MapHeaders from './mapHeaders';

interface HandleFileProps {
  businessUnitId: number;
  outboundCampaignId: string;
  onSuccess: () => void;
}

const HandleFile = ({ businessUnitId, outboundCampaignId, onSuccess }: HandleFileProps) => {
  const [source, setSource] = useState<string | null>(null);
  const [fileToSend, setFileToSend] = useState<File | null>(null);
  const [separator, setSeparator] = useState<string>(';');
  const [headers, setHeaders] = useState<string[]>([]);
  const [exampleData, setExampleData] = useState<string[][]>([]);
  const [mappedHeaders, setMappedHeaders] = useState<{ headerName: string; attrCliente: string }[]>([]);
  const [showError, setShowError] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const [uploadClientsFromCSV, { isError, error, isSuccess, isLoading }] = useUploadClientsFromCSVMutation();
  const [mapCSVHeaders, { data: newMappedHeaders }] = useMapCSVHeadersMutation();
  const [getBusinessUnitById, { data: businessUnitData }] = useGetBusinessUnitByIdMutation();

  const hasPhoneAttr = mappedHeaders.find((h) => h.attrCliente === 'phone');

  useEffect(() => {
    getBusinessUnitById(businessUnitId).catch((err) => console.error(err)); // eslint-disable-line no-console
  }, [businessUnitId]);

  const parseFile = async (file: File) => {
    const text = await file.text();
    const rows = text.split('\n');
    const newSeparator = rows[0].split(',').length > rows[0].split(';').length ? ',' : ';';
    const newHeaders = rows[0].split(newSeparator);
    const newExampleData = rows.slice(1, 6).map((row) => row.split(newSeparator));
    setSeparator(newSeparator);
    setHeaders(newHeaders);
    setExampleData(newExampleData);
    mapCSVHeaders({
      headers: newHeaders,
      exampleData: newExampleData,
    }).catch((e) => {
      // eslint-disable-next-line no-console
      console.error(e);
    });
  };

  useEffect(() => {
    if (fileToSend) {
      parseFile(fileToSend).catch((e) => {
        // eslint-disable-next-line no-console
        console.error(e);
      });
    }
  }, [fileToSend]);

  useEffect(() => {
    if (newMappedHeaders?.mappedHeaders) {
      setMappedHeaders(newMappedHeaders.mappedHeaders);
    }
  }, [newMappedHeaders]);

  useEffect(() => {
    if (isError) {
      setShowError(true);
    }
    if (isSuccess) {
      setShowSuccess(true);
    }
  }, [isError, isSuccess]);

  return (
    <Box pl={2} py={2}>
      <AlertDialog
        openDialog={showError}
        setOpenDialog={setShowError}
        header="Error al subir archivo"
        msg={JSON.stringify(error)}
        cancelMsg="Cerrar"
      />
      {showSuccess && (
        <InfoDialog
          header="Archivo subido exitosamente"
          msg="El archivo se ha subido correctamente"
          onClose={() => {
            setShowSuccess(false);
            onSuccess();
          }}
        />
      )}
      <Box display="flex" alignItems="center">
        <Typography variant="h6">Subir base para campaña</Typography>
      </Box>
      <Typography pb={1} variant="body1">
        Define un origen para los clientes, te ayudará a identificarlos en la plataforma:
      </Typography>
      <TextField label="Origen" value={source || ''} onChange={(e) => setSource(e.target.value)} type="text" required />
      <Box display="flex" alignItems="center">
        <Typography variant="h6" py={1}>
          Subir archivo csv con clientes
        </Typography>
        <label htmlFor="file-input">
          <input
            id="file-input"
            name="file-input"
            type="file"
            style={{ display: 'none' }}
            accept=".csv"
            onChange={(e) => {
              if (e.target.files) {
                setFileToSend(e.target.files[0]);
              }
            }}
          />
          <Button variant="text" component="span">
            <AttachFileIcon fontSize="large" />
          </Button>
        </label>
        {fileToSend && <Typography variant="body2">{fileToSend.name}</Typography>}
      </Box>
      {headers && exampleData && fileToSend && (
        <Box my={1} mr={2}>
          <Typography variant="body1">Primeras 5 filas de {fileToSend.name}</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {headers.map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {exampleData.map((row) => (
                  <TableRow key={row.join('')}>
                    {row.map((cell) => (
                      <TableCell key={`${cell}-${Math.random()}`}>{cell}</TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {headers.length > 0 && exampleData.length > 0 && (
        <MapHeaders
          rows={headers.map((header, index) => ({
            headerName: header.trim(),
            examples: exampleData.map((row) => row[index]?.trim() || ''),
          }))}
          mappedHeaders={mappedHeaders}
          setMappedHeaders={setMappedHeaders}
          businessUnitType={businessUnitData?.type || 'car_dealership'}
        />
      )}
      {fileToSend && source && (
        <Box display="flex" alignItems="center">
          <Button
            variant="outlined"
            onClick={() => {
              if (!hasPhoneAttr) return;
              uploadClientsFromCSV({
                businessUnitId,
                outboundCampaignId,
                source,
                mappedHeaders,
                separator,
                file: fileToSend,
              }).catch((e) => {
                // eslint-disable-next-line no-console
                console.error(e);
                setShowError(true);
              });
            }}
            disabled={!hasPhoneAttr || isLoading}
            startIcon={<UploadIcon />}
          >
            Subir archivo
          </Button>
          {!hasPhoneAttr && (
            <Typography variant="body1" color="error" pl={1}>
              *No se ha seleccionado el atributo de teléfono, por lo que no se puede subir el archivo
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

export default HandleFile;
